import {
  MESSAGING_WIDGET,
  MessagingWidgetState,
  SelectedSearchResult,
  ThreadSelection,
} from '@hb/shared'
import { useCallback, useContext, useMemo, useState } from 'react'
import { useApp } from '../../contexts/AppContext'
import { ScreenContext } from '../../contexts/ScreenContext'

import { useOptimisticUpdate } from '../../backend/db/hooks/useOptimisticUpdate'
import { useAuth } from '../../store/auth'
import { MessagingWidget, SearchMyThreads } from '../types/MessagingWidget'

export const useMessagingWidget = (
  uid: string | null,
  searchMyThreads: SearchMyThreads,
): MessagingWidget => {
  const { appName } = useApp()
  const { accessLevel } = useAuth()
  const collectionPath = useMemo(() => `${MESSAGING_WIDGET}/${uid}/apps`, [uid])
  const {
    data,
    update: _update,
    ...rest
  } = useOptimisticUpdate<MessagingWidgetState>(collectionPath, uid ? appName : null)

  const [localNotificationVolume, setLocalNotificationVolume] = useState<number | null>(null)

  const [threadSelection, setThreadSelection] = useState<ThreadSelection | null>(null)

  const [searchResult, setSearchResult] = useState<SelectedSearchResult | null>(null)

  const [creatingNewThread, setCreatingNewThread] = useState(false)

  const withDefaults = useMemo<MessagingWidgetState>(
    () => ({
      tab: accessLevel === 'admin' ? 'admin' : 'practice',
      notificationVolume: data?.notificationVolume ?? 0.25,
      ...data,
      // thread: data?.thread ?? null,
    }),
    [data, accessLevel],
  )
  const { isMobile } = useContext(ScreenContext)

  const [searchQuery, setSearchQuery] = useState('')
  const updateSearchQuery = useCallback((updated: string) => {
    setSearchQuery(updated)
  }, [])

  const tabsVisible = useMemo(
    () => !searchQuery && !creatingNewThread && (!isMobile || !threadSelection),
    [searchQuery, isMobile, creatingNewThread, threadSelection],
  )

  const update = useCallback(
    async (updated: Partial<MessagingWidgetState>) => _update(updated),
    [_update],
  )

  return {
    ...rest,
    update,
    searchMyThreads,
    localNotificationVolume,
    clearSearchResult: () => setSearchResult(null),
    searchResult,
    setSearchResult,
    updateSearchQuery,
    tabsVisible,
    searchQuery,
    creatingNewThread,
    setCreatingNewThread,
    updateLocalNotificationVolume: setLocalNotificationVolume,
    data: withDefaults,
    threadSelection,
    setThreadSelection,
  }
}
